import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { merge } from "lodash";

import { getCurrentLang } from "/@/utils/currentLang";
import { normalizeLang } from "/@/utils/normalizeLang";
import en from "./lang/en";
import es from "./lang/es";
import ja from "./lang/ja";
import ko from "./lang/ko";
import zh from "./lang/zh";
import zhHK from "./lang/zh-HK";
import zhTW from "./lang/zh-TW";
import threeViewer from "./lang/threeViewer";

function hasDefaultValue(args: any[]): boolean {
	return (
		typeof args[0] === "string" ||
		(args[0] && typeof args[0] === "object" && "defaultValue" in args[0])
	);
}

export function wrapI18nT(i18nInstance: typeof i18n, zhFallback: Record<string, any>) {
	const rawT = i18nInstance.t.bind(i18nInstance);
	i18nInstance.t = ((key: string | string[], arg1?: any, ...rest: any[]) => {
		const args = arg1 === undefined ? [] : [arg1, ...rest];
		const result = String(rawT(key as any, ...(args as [any])));
		if (typeof key !== "string" || hasDefaultValue(args) || result !== key) {
			return result;
		}
		const zhValue = zhFallback?.[key];
		if (zhValue) {
			return String(rawT(key, { defaultValue: zhValue }));
		}
		return result;
	}) as typeof i18nInstance.t;
}

const create = async (data: any) => {
	const messages: Record<string, any> = {
		"zh-CN": zh,
		"zh-TW": zhTW,
		"zh-HK": zhHK,
		en,
		es,
		ja,
		ko
	};
	const initialLanguage = getCurrentLang() || "zh-CN";

	Object.keys(data || {}).forEach((key) => {
		const lang = normalizeLang(key);
		if (messages[lang]) {
			merge(messages[lang], data[key]);
		}
	});

	Object.keys(threeViewer).forEach((lang) => {
		if (messages[lang]) {
			merge(messages[lang], threeViewer[lang as keyof typeof threeViewer]);
		}
	});

	await i18n.use(LanguageDetector).init({
		resources: {
			en: { translation: messages.en },
			"zh-CN": { translation: messages["zh-CN"] },
			"zh-HK": { translation: messages["zh-HK"] || messages["zh-TW"] },
			"zh-TW": { translation: messages["zh-TW"] },
			es: { translation: messages.es },
			ko: { translation: messages.ko },
			"ko-KR": { translation: messages.ko },
			kr: { translation: messages.ko },
			ja: { translation: messages.ja },
			"ja-JP": { translation: messages.ja },
			jp: { translation: messages.ja }
		},
		fallbackLng: "zh-CN",
		supportedLngs: [
			"en",
			"zh-CN",
			"zh-HK",
			"zh-TW",
			"es",
			"ko",
			"ko-KR",
			"kr",
			"ja",
			"ja-JP",
			"jp"
		],
		nonExplicitSupportedLngs: true,
		lng: initialLanguage,
		debug: false,
		react: {
			useSuspense: false
		},
		detection: {
			order: ["querystring", "localStorage", "navigator"],
			lookupQuerystring: "lang",
			lookupLocalStorage: "i18nextLng",
			caches: ["localStorage"]
		}
	});

	await i18n.changeLanguage(initialLanguage);
	wrapI18nT(i18n, messages["zh-CN"]);
	return i18n;
};

export default create;
